import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, Headphones, Menu, Megaphone, MoreHorizontal, Terminal, User, Presentation } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LogoShield } from "@/components/LogoShield";
import { FeatureRequestDialog } from "@/components/portal/FeatureRequestDialog";
import { SupportDialog } from "@/components/portal/SupportDialog";
import { AuthenticatedHeader } from "@/components/portal/AuthenticatedHeader";
import type { AccountMenuContext } from "@/components/portal/AccountMenu";

type PortalTopNavProps = {
  userEmail: string;
  userName?: string;
  isAdmin?: boolean;
  context?: AccountMenuContext;
  onExitImpersonation?: () => void;
  active?: "dashboard" | "billing" | "account" | "autoscan" | "promotions" | "demo";
  showDemo?: boolean;
};

export function PortalTopNav({ userEmail, userName, isAdmin, context, onExitImpersonation, active, showDemo }: PortalTopNavProps) {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [featureOpen, setFeatureOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);

  const navButtonVariant = (key: PortalTopNavProps["active"]) =>
    active === key ? "secondary" : "outline";

  const navContext: AccountMenuContext =
    context ?? (isAdmin === true ? "admin" : "customer");

  const roleLabel = isAdmin === true ? "Admin" : undefined;
  const shouldShowDemo = showDemo ?? userEmail.toLowerCase().endsWith("@shadowscan.com.au");

  return (
    <>
      <AuthenticatedHeader
        userEmail={userEmail}
        userName={userName}
        context={navContext}
        roleLabel={roleLabel}
        onExitImpersonation={onExitImpersonation}
        leftSlot={(
          <button
            type="button"
            className="flex items-center gap-3"
            onClick={() => navigate("/portal")}
          >
            <LogoShield className="h-7 w-7" />
            <span className="text-xl font-heading font-bold tracking-tight">
              <span className="text-foreground">Shadow</span>
              <span className="text-primary">Scan</span>
              <span className="text-muted-foreground"> Portal</span>
            </span>
          </button>
        )}
        centerSlot={(
          <>
            {shouldShowDemo && (
              <Button
                variant={navButtonVariant("demo")}
                size="sm"
                onClick={() => navigate("/portal/demo")}
              >
                <Presentation className="h-4 w-4 mr-2" />
                Demo
              </Button>
            )}
            {isAdmin === true && (
              <>
                <Button
                  variant={navButtonVariant("promotions")}
                  size="sm"
                  onClick={() => navigate("/portal/promotions")}
                >
                  <Megaphone className="h-4 w-4 mr-2" />
                  Promotions
                </Button>
                <Button
                  variant={navButtonVariant("autoscan")}
                  size="sm"
                  onClick={() => navigate("/portal/autoscan")}
                >
                  <Terminal className="h-4 w-4 mr-2" />
                  Live Scan
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm">
                      <MoreHorizontal className="h-4 w-4 mr-2" />
                      More
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => navigate("/portal/account")}>
                      <User className="h-4 w-4 mr-2" />
                      Account
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/portal/security-audit")}>
                      <FileText className="h-4 w-4 mr-2" />
                      Audit Logs
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
            {isAdmin !== true && (
              null
            )}
          </>
        )}
        rightSlot={isAdmin !== true ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2" aria-label="Contact">
                <Headphones className="h-4 w-4" />
                <span className="hidden sm:inline">Contact</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => setFeatureOpen(true)}>
                Feature Request
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setSupportOpen(true)}>
                Support
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      />

      <div className="sm:hidden relative z-40 w-full flex justify-end gap-2 px-4 pt-3">
        {(isAdmin === true || shouldShowDemo) && (
          <>
            <Button variant="outline" size="sm" onClick={() => setMenuOpen((v) => !v)}>
              <Menu className="h-4 w-4 mr-2" />
              Menu
            </Button>
            {menuOpen && (
              <div className="absolute right-4 mt-10 w-56 max-w-[calc(100vw-2rem)] rounded-lg border border-border bg-background shadow-lg z-50 overflow-hidden">
                <div className="flex flex-col divide-y divide-border">
                  {shouldShowDemo && (
                    <button
                      className="flex items-center gap-2 px-4 py-2 text-left hover:bg-muted/50"
                      onClick={() => {
                        setMenuOpen(false);
                        navigate("/portal/demo");
                      }}
                    >
                      <Presentation className="h-4 w-4" /> Demo
                    </button>
                  )}
                  {isAdmin === true && (
                    <>
                      <button
                        className="flex items-center gap-2 px-4 py-2 text-left hover:bg-muted/50"
                        onClick={() => {
                          setMenuOpen(false);
                          navigate("/portal/promotions");
                        }}
                      >
                        <Megaphone className="h-4 w-4" /> Promotions
                      </button>
                      <button
                        className="flex items-center gap-2 px-4 py-2 text-left hover:bg-muted/50"
                        onClick={() => {
                          setMenuOpen(false);
                          navigate("/portal/autoscan");
                        }}
                      >
                        <Terminal className="h-4 w-4" /> Live Scan
                      </button>
                      <button
                        className="flex items-center gap-2 px-4 py-2 text-left hover:bg-muted/50"
                        onClick={() => {
                          setMenuOpen(false);
                          navigate("/portal/security-audit");
                        }}
                      >
                        <FileText className="h-4 w-4" /> Audit Logs
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {isAdmin !== true && (
        <>
          <FeatureRequestDialog
            userEmail={userEmail}
            open={featureOpen}
            onOpenChange={setFeatureOpen}
            trigger={null}
          />
          <SupportDialog
            userEmail={userEmail}
            open={supportOpen}
            onOpenChange={setSupportOpen}
            trigger={null}
          />
        </>
      )}
    </>
  );
}
